import axios from 'axios';

// App version - increment this when you want to force frontend updates UPDATE VERSION MARK
export const APP_VERSION = '1.0.0';
export const MIN_COMPATIBLE_VERSION = '1.0.0';

// Create axios instance with default config
// Auto-detect production environment and use appropriate API URL
const getApiBaseUrl = () => {
  // If environment variable is set, use it
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // Auto-detect based on current hostname (fallback to same-origin)
  if (window?.location?.origin && window.location.origin.startsWith('http')) {
    // Assume API is hosted on same origin unless overridden
    return window.location.origin.replace(/:\d+$/, ':5000');
  }
  
  // Default to localhost for development
  return 'http://localhost:5000';
};

const baseURL = getApiBaseUrl();
console.log(`🔗 API Client initialized with baseURL: ${baseURL}`);

const apiClient = axios.create({
  baseURL: baseURL,
  timeout: 30000, // 30 second timeout
});

// Add version header to all requests
apiClient.interceptors.request.use(
  (config) => {
    // Add app version to all requests
    config.headers['X-App-Version'] = APP_VERSION;
    config.headers['X-App-Build-Time'] = process.env.REACT_APP_BUILD_TIME || Date.now();
    
    // Add user agent info for better debugging
    if (typeof navigator !== 'undefined') {
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                           window.navigator.standalone === true;
      
      if (isIOS && isStandalone) {
        config.headers['X-App-Platform'] = 'ios-homescreen';
      } else if (isIOS) {
        config.headers['X-App-Platform'] = 'ios-safari';
      } else {
        config.headers['X-App-Platform'] = 'web';
      }
    }
    
    console.log(`🔗 API Request: ${config.method?.toUpperCase()} ${config.url} (v${APP_VERSION})`);
    return config;
  },
  (error) => {
    console.error('🔗 API Request Error:', error);
    return Promise.reject(error);
  }
);

// Handle responses and version rejection
apiClient.interceptors.response.use(
  (response) => {
    // Check if server sent back version info
    const serverMinVersion = response.headers['x-min-app-version'];
    const serverCurrentVersion = response.headers['x-current-app-version'];
    
    if (serverMinVersion && isVersionOutdated(APP_VERSION, serverMinVersion)) {
      console.warn(`🔄 App version ${APP_VERSION} is below minimum required ${serverMinVersion}`);
      // Don't block the current request, but warn about upcoming version requirement
      showVersionWarning(serverMinVersion, serverCurrentVersion);
    }
    
    return response;
  },
  async (error) => {
    console.error('🔗 API Response Error:', error);
    
    // Handle version rejection (426 Upgrade Required)
    if (error.response?.status === 426) {
      const requiredVersion = error.response.data?.requiredVersion || 'latest';
      const currentServerVersion = error.response.data?.currentVersion;
      
      console.error(`🚫 API Version Rejection: App version ${APP_VERSION} is too old. Required: ${requiredVersion}`);
      
      // Trigger cache update flow
      await handleVersionRejection(requiredVersion, currentServerVersion);
      
      // Return a more user-friendly error
      const versionError = new Error('App version outdated - please update');
      versionError.code = 'VERSION_OUTDATED';
      versionError.requiredVersion = requiredVersion;
      versionError.currentVersion = APP_VERSION;
      throw versionError;
    }
    
    // Handle other errors
    if (error.response?.status === 401) {
      console.warn('🔒 Authentication required');
      // Let the auth context handle this
    } else if (error.response?.status >= 500) {
      console.error('🔥 Server error:', error.response.status);
    } else if (!error.response) {
      console.error('🌐 Network error or server unreachable');
    }
    
    return Promise.reject(error);
  }
);

// Version comparison utility
function isVersionOutdated(currentVersion, minVersion) {
  const current = parseVersion(currentVersion);
  const minimum = parseVersion(minVersion);
  
  if (current.major < minimum.major) return true;
  if (current.major > minimum.major) return false;
  
  if (current.minor < minimum.minor) return true;
  if (current.minor > minimum.minor) return false;
  
  return current.patch < minimum.patch;
}

function parseVersion(version) {
  const [major, minor, patch] = version.split('.').map(Number);
  return { major: major || 0, minor: minor || 0, patch: patch || 0 };
}

// Show version warning (non-blocking)
function showVersionWarning(requiredVersion, currentServerVersion) {
  // Check if we already showed this warning recently
  const warningKey = `version-warning-${requiredVersion}`;
  const lastWarning = localStorage.getItem(warningKey);
  const now = Date.now();
  
  // Don't spam warnings - only show once per hour
  if (lastWarning && (now - parseInt(lastWarning)) < 3600000) {
    return;
  }
  
  localStorage.setItem(warningKey, now.toString());
  
  // Create non-intrusive warning notification
  const warning = document.createElement('div');
  warning.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: var(--warning-color, #ff9800);
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 9999;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    max-width: 300px;
    cursor: pointer;
  `;
  
  warning.innerHTML = `
    <div style="font-weight: 500; margin-bottom: 4px;">⚠️ Update Recommended</div>
    <div style="font-size: 13px; opacity: 0.9;">
      App version ${APP_VERSION} will be unsupported soon. Please update to v${currentServerVersion || requiredVersion}
    </div>
  `;
  
  warning.onclick = () => {
    if (window.forceCacheRefresh) {
      window.forceCacheRefresh().catch(console.error);
    }
    warning.remove();
  };
  
  document.body.appendChild(warning);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    if (warning.parentNode) {
      warning.remove();
    }
  }, 10000);
}

// Handle version rejection (blocking)
async function handleVersionRejection(requiredVersion, currentServerVersion) {
  console.log('🔄 Handling version rejection...');
  
  // Remove any existing version modals
  const existingModal = document.getElementById('version-rejection-modal');
  if (existingModal) {
    existingModal.remove();
  }
  
  // Create blocking modal
  const modal = document.createElement('div');
  modal.id = 'version-rejection-modal';
  modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  `;
  
  const modalContent = document.createElement('div');
  modalContent.style.cssText = `
    background: white;
    padding: 24px;
    border-radius: 12px;
    max-width: 400px;
    margin: 20px;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  `;
  
  modalContent.innerHTML = `
    <div style="font-size: 48px; margin-bottom: 16px;">🚫</div>
    <h2 style="color: var(--danger-color, #d32f2f); margin: 0 0 16px 0;">App Update Required</h2>
    <p style="color: #666; margin: 0 0 20px 0; line-height: 1.5;">
      Your app version (${APP_VERSION}) is no longer supported. 
      Please update to version ${currentServerVersion || requiredVersion} to continue.
    </p>
    <div style="display: flex; gap: 12px; justify-content: center;">
      <button id="update-now-btn" style="
        background: var(--secondary-color, #2196f3);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-weight: 500;
        cursor: pointer;
        font-size: 14px;
      ">Update Now</button>
      <button id="reload-btn" style="
        background: var(--gray-600, #6c757d);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 6px;
        font-weight: 500;
        cursor: pointer;
        font-size: 14px;
      ">Reload Page</button>
    </div>
    <div style="margin-top: 16px; font-size: 13px; color: var(--gray-500, #999);">
      This ensures you have the latest features and security updates.
    </div>
  `;
  
  modal.appendChild(modalContent);
  document.body.appendChild(modal);
  
  // Handle button clicks
  const updateBtn = modal.querySelector('#update-now-btn');
  const reloadBtn = modal.querySelector('#reload-btn');
  
  updateBtn.onclick = async () => {
    updateBtn.textContent = 'Updating...';
    updateBtn.disabled = true;
    
    try {
      if (window.forceCacheRefresh) {
        await window.forceCacheRefresh();
        window.location.reload();
      } else {
        // Fallback: force reload
        window.location.reload(true);
      }
    } catch (error) {
      console.error('Cache refresh failed:', error);
      // Fallback to hard reload
      window.location.reload(true);
    }
  };
  
  reloadBtn.onclick = () => {
    window.location.reload(true);
  };
  
  // Prevent closing the modal by clicking outside
  modal.onclick = (e) => {
    if (e.target === modal) {
      // Don't allow closing - force the update
      updateBtn.style.animation = 'pulse 0.5s';
      setTimeout(() => {
        if (updateBtn.style) {
          updateBtn.style.animation = '';
        }
      }, 500);
    }
  };
}

// Utility function to check current app version
export function getCurrentAppVersion() {
  return APP_VERSION;
}

// Utility function to check if version checking is enabled
export function isVersionCheckingEnabled() {
  return true; // Can be controlled by environment variable
}

// Export configured axios instance
export default apiClient; 